import { Close as CloseIcon, Delete as DeleteIcon, Folder as FolderIcon } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { SearchField } from "~ui/atoms/SearchField/SearchField";
import { ConfirmDeleteDialog } from "~ui/molecules/ConfirmDeleteDialog/ConfirmDeleteDialog";
import { ResourceFormDialog } from "~ui/molecules/ResourceFormDialog/ResourceFormDialog";

import { type ResourcesSlideoutProps } from "./types";
import { useResourcesSlideout } from "./useResourcesSlideout";

// Centralized strings for internationalization
const tAddFirstResource = "Add your first resource to get started";
const tDeleteResource = "Delete resource";
const tNoResources = "No resources yet";
const tNoResourcesFound = "No resources found";
const tResources = "Resources";
const tResourcesWillAppear = "Resources will appear here when added";
const tSearchResources = "Search resources...";
const tTryDifferentSearch = "Try searching with different keywords";

export const ResourcesSlideout = ({
  open,
  onClose,
  resources,
  canManage = false,
  onAddResource,
  onDeleteResource,
}: ResourcesSlideoutProps) => {
  const {
    deleteDialogOpen,
    filteredResources,
    getFaviconUrl,
    handleDeleteCancel,
    handleDeleteClick,
    handleDeleteConfirm,
    resourceToDelete,
    searchQuery,
    setSearchQuery,
  } = useResourcesSlideout(resources);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      role="complementary"
      aria-label="Resources"
      sx={{
        "& .MuiDrawer-paper": {
          borderRadius: "16px 0 0 16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          width: { sm: 400, xs: "100%" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            alignItems: "center",
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            p: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <FolderIcon sx={{ fontSize: "2rem" }} />
            <Box>
              <Typography variant="h2" component="h2" sx={{ fontSize: "1.25rem", fontWeight: "bold" }}>
                {tResources}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {resources.length} {resources.length === 1 ? "resource" : "resources"}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            aria-label="Close resources"
            sx={{
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Add Resource Button */}
        {canManage && onAddResource && (
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", p: 2 }}>
            <ResourceFormDialog onAddResource={onAddResource} />
          </Box>
        )}

        {/* Search Field */}
        <Box sx={{ borderBottom: "1px solid", borderColor: "divider", p: 2 }}>
          <SearchField value={searchQuery} onChange={setSearchQuery} placeholder={tSearchResources} size="small" />
        </Box>

        {/* Resources List */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {/* No Search Results State */}
          {searchQuery && filteredResources.length === 0 && (
            <Box
              role="status"
              aria-label="No resources found"
              sx={{
                alignItems: "center",
                color: "text.secondary",
                display: "flex",
                flexDirection: "column",
                height: "200px",
                justifyContent: "center",
              }}
            >
              <FolderIcon sx={{ fontSize: "4rem", mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                {tNoResourcesFound}
              </Typography>
              <Typography variant="body2" textAlign="center">
                {tTryDifferentSearch}
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {!searchQuery && resources.length === 0 && (
            <Box
              role="status"
              aria-label="No resources"
              sx={{
                alignItems: "center",
                color: "text.secondary",
                display: "flex",
                flexDirection: "column",
                height: "200px",
                justifyContent: "center",
              }}
            >
              <FolderIcon sx={{ fontSize: "4rem", mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                {tNoResources}
              </Typography>
              <Typography variant="body2" textAlign="center">
                {canManage ? tAddFirstResource : tResourcesWillAppear}
              </Typography>
            </Box>
          )}

          {/* Resources List */}
          {filteredResources.length > 0 && (
            <List sx={{ p: 0 }}>
              {filteredResources.map((resource, index) => (
                <ListItem key={resource.id} sx={{ p: 0 }}>
                  <Card
                    data-testid={`resource-item-${index}`}
                    sx={{
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-2px)",
                      },
                      borderLeft: "4px solid",
                      borderLeftColor: "primary.main",
                      m: 1,
                      transition: "all 0.2s ease",
                      width: "100%",
                    }}
                  >
                    <CardContent sx={{ "&:last-child": { pb: 2 }, p: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        {/* Favicon */}
                        <Box
                          component="img"
                          src={getFaviconUrl(resource.url) || undefined}
                          alt=""
                          sx={{
                            borderRadius: 1,
                            height: 24,
                            mt: 0.5,
                            width: 24,
                          }}
                          onError={(e) => {
                            // Hide image if favicon fails to load
                            e.currentTarget.style.display = "none";
                          }}
                        />

                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="h3"
                            component="h3"
                            data-testid={`resource-name-${index}`}
                            sx={{
                              fontSize: "1rem",
                              fontWeight: "bold",
                              mb: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {resource.name}
                          </Typography>

                          {resource.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              data-testid={`resource-description-${index}`}
                              sx={{
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                                display: "-webkit-box",
                                mb: 1,
                                overflow: "hidden",
                              }}
                            >
                              {resource.description}
                            </Typography>
                          )}

                          <Link
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`resource-url-${index}`}
                            sx={{
                              "&:hover": { textDecoration: "underline" },
                              color: "primary.main",
                              display: "block",
                              fontSize: "0.75rem",
                              overflow: "hidden",
                              textDecoration: "none",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {resource.url}
                          </Link>
                        </Box>

                        {/* Actions */}
                        <Stack direction="row" spacing={0.5}>
                          {canManage && (
                            <Tooltip title={tDeleteResource}>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(resource)}
                                data-testid={`delete-resource-${index}`}
                                sx={{ color: "error.main" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={() => handleDeleteConfirm(onDeleteResource)}
        itemType="resource"
        itemName={resourceToDelete?.name || ""}
        itemDescription={resourceToDelete?.description || undefined}
      />
    </Drawer>
  );
};
